"use client";

import React from 'react';
import Link from 'next/link';
import Image from 'next/image';
import DiscordCommunitySection from '../components/DiscordCommunitySection';
import { WalletIdenticon } from '../components/WalletIdenticon';
import { ScrollReveal } from '../../components/shared';
import {
  PhoneIcon,
  SearchIcon,
  GlobeIcon,
  CopyIcon,
  CheckCircleIcon,
  UserIcon,
} from '../../components/shared/icons';

type Entry = {
  address: string;
  displayName: string | null;
  avatarUrl: string | null;
  udDomain: string | null;
  bio: string | null;
  website: string | null;
  twitter: string | null;
  telegram: string | null;
  discord: string | null;
  phoneApps: string[];
  updatedAt: string | null;
};

type ApiPayload = {
  page: number;
  limit: number;
  total: number;
  results: Entry[];
  resolvedAddress: string | null;
};

function short(addr: string) {
  return addr.slice(0, 6) + '…' + addr.slice(-4);
}

/** Strip a leading @ and any full URL so a handle can be appended to a base. */
function handleOnly(raw: string): string {
  return String(raw || '')
    .trim()
    .replace(/^https?:\/\/(www\.)?(x\.com|twitter\.com|t\.me|telegram\.me)\//i, '')
    .replace(/^@/, '')
    .replace(/\/+$/, '');
}

function twitterUrl(raw: string) {
  const h = handleOnly(raw);
  return h ? `https://x.com/${encodeURIComponent(h)}` : null;
}

function telegramUrl(raw: string) {
  const h = handleOnly(raw);
  return h ? `https://t.me/${encodeURIComponent(h)}` : null;
}

/** Only allow http(s) so a stored `javascript:` value can never be rendered. */
function safeWebsite(raw: string | null): string | null {
  if (!raw) return null;
  const v = raw.trim();
  const withProto = /^https?:\/\//i.test(v) ? v : `https://${v}`;
  try {
    const u = new URL(withProto);
    if (u.protocol !== 'http:' && u.protocol !== 'https:') return null;
    return u.toString();
  } catch {
    return null;
  }
}

/**
 * Web3 Phone Book — opt-in directory of wallet profiles.
 *
 * 2026-08-05 rebuild. What was wrong beyond the styling:
 *
 *  - Every page load fired TWO identical requests: a mount effect calling
 *    load(1, '') plus the debounce effect, which also runs on mount with an
 *    empty query.
 *  - No request sequencing. Typing quickly meant several searches in flight
 *    at once, and a slow earlier response could land after a fast later one
 *    and overwrite the correct results with stale ones.
 *  - The social handles were inert `<span>` chips - the card was one big
 *    `<a>`, and an anchor can't legally contain another anchor, so a
 *    directory built for contacting people had no way to reach anyone.
 *  - `website` was returned by the API and never rendered at all.
 *  - Subsequent searches showed the previous results with no loading
 *    indication, so the page looked frozen.
 *  - There was no way to copy an address from a directory of addresses.
 */
export default function PhoneBookPage() {
  const [q, setQ] = React.useState('');
  const [page, setPage] = React.useState(1);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [data, setData] = React.useState<ApiPayload | null>(null);
  const [copied, setCopied] = React.useState<string | null>(null);

  const searchRef = React.useRef<HTMLInputElement | null>(null);
  // Monotonic request id. Only the newest response is allowed to write state,
  // which is what kills the stale-overwrite race described above.
  const reqIdRef = React.useRef(0);

  const load = React.useCallback(async (nextPage: number, nextQ: string) => {
    const myId = ++reqIdRef.current;
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (nextQ.trim()) params.set('q', nextQ.trim());
      params.set('page', String(nextPage));
      params.set('limit', '20');
      const res = await fetch(`/api/phone-book?${params.toString()}`, { cache: 'no-store' });
      const json = (await res.json().catch(() => ({}))) as ApiPayload;
      if (myId !== reqIdRef.current) return; // a newer request has superseded this one
      if (!res.ok) {
        setError((json as unknown as { message?: string })?.message || 'Failed to load directory');
        setData(null);
      } else {
        setData(json);
      }
    } catch {
      if (myId !== reqIdRef.current) return;
      setError('Failed to load directory');
      setData(null);
    } finally {
      if (myId === reqIdRef.current) setLoading(false);
    }
  }, []);

  // Single source of truth for fetching: the debounced query. This effect
  // also covers the initial load (q === '' on mount), so there is no longer
  // a separate mount effect duplicating the first request.
  React.useEffect(() => {
    const t = window.setTimeout(() => {
      setPage(1);
      void load(1, q);
    }, q ? 250 : 0);
    return () => window.clearTimeout(t);
  }, [q, load]);

  // "/" focuses search, Escape clears it — standard for a directory.
  React.useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const el = document.activeElement;
      const typing = el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement;
      if (e.key === '/' && !typing) {
        e.preventDefault();
        searchRef.current?.focus();
      } else if (e.key === 'Escape' && el === searchRef.current) {
        setQ('');
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  async function copyAddress(address: string) {
    try {
      if (!navigator?.clipboard?.writeText) throw new Error('unavailable');
      await navigator.clipboard.writeText(address);
      setCopied(address);
      setTimeout(() => setCopied((c) => (c === address ? null : c)), 1800);
    } catch {
      setCopied(null);
    }
  }

  const results = data?.results || [];
  const total = data?.total || 0;
  const limit = data?.limit || 20;
  const maxPage = Math.max(1, Math.ceil(total / limit));
  const showSkeletons = loading && results.length === 0;

  return (
    <div
      className="flex min-h-screen flex-col"
      style={{
        backgroundColor: '#0A0F1C',
        backgroundImage:
          'radial-gradient(circle at top, rgba(0,212,255,0.08), transparent 52%), radial-gradient(circle at bottom, rgba(124,58,237,0.06), transparent 55%)',
      }}
    >
      <main className="flex w-full flex-1 flex-col items-center">
        {/* ── Hero ─────────────────────────────────────────────────────── */}
        <section aria-labelledby="phone-book-heading" className="page-section relative w-full pt-8 pb-5 sm:pb-6">
          <div className="mx-auto max-w-6xl">
            <ScrollReveal>
              <div
                className="section-panel relative overflow-hidden rounded-[32px] border px-5 py-8 sm:px-10 sm:py-10"
                style={{
                  borderColor: 'rgba(0,212,255,0.24)',
                  background: 'linear-gradient(180deg, rgba(20,32,60,0.9), rgba(10,16,32,0.95))',
                  boxShadow:
                    '0 0 0 1px rgba(0,212,255,0.08) inset, 0 30px 70px -30px rgba(0,0,0,0.85)',
                }}
              >
                <div className="pointer-events-none absolute inset-0 -z-10" aria-hidden="true">
                  <div
                    className="absolute -top-24 left-1/3 h-72 w-72 rounded-full opacity-40 blur-3xl"
                    style={{ background: 'radial-gradient(circle, rgba(0,212,255,0.22), transparent 70%)' }}
                  />
                </div>

                <div className="relative z-10">
                  <span className="eyebrow-label">
                    <PhoneIcon size={14} />
                    Opt-in directory
                  </span>

                  <h1
                    id="phone-book-heading"
                    className="headline-balance mt-5 text-4xl font-extrabold leading-[1.02] tracking-tight sm:text-5xl"
                    style={{ color: '#f2f6ff' }}
                  >
                    Web3 Phone Book
                  </h1>
                  <p className="pr-section-sub">
                    Find people, not just addresses. Every profile here was listed by its owner — search by name,
                    address, Unstoppable Domain, social handle, or messaging app.
                  </p>

                  <div className="pr-highlight-grid mt-7 max-w-2xl">
                    <div className="pr-highlight">
                      <span className="pr-highlight-icon" aria-hidden="true">
                        <UserIcon size={15} />
                      </span>
                      <span className="pr-highlight-body">
                        <span className="pr-highlight-eyebrow">Listed profiles</span>
                        <span className="pr-highlight-text">
                          {loading && !data ? 'Loading directory…' : `${total} ${total === 1 ? 'profile' : 'profiles'} opted in`}
                        </span>
                      </span>
                    </div>
                    <div className="pr-highlight">
                      <span className="pr-highlight-icon" aria-hidden="true">
                        <CheckCircleIcon size={15} />
                      </span>
                      <span className="pr-highlight-body">
                        <span className="pr-highlight-eyebrow">Consent first</span>
                        <span className="pr-highlight-text">Nobody appears here without enabling it themselves</span>
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </ScrollReveal>
          </div>
        </section>

        {/* ── Directory ────────────────────────────────────────────────── */}
        <section className="page-section relative w-full pb-16">
          <div className="mx-auto grid max-w-6xl grid-cols-1 items-start gap-6 lg:grid-cols-[minmax(0,1fr)_20rem]">
            <div className="min-w-0">
              {/* Search */}
              <div className="pr-panel">
                <label htmlFor="phone-book-search" className="pr-label">
                  Search the directory
                </label>
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                  <div
                    className="flex min-h-[2.75rem] min-w-0 flex-1 items-center gap-2.5 rounded-xl border px-4 py-2.5"
                    style={{ borderColor: 'rgba(0,212,255,0.28)', background: 'rgba(3,8,20,0.6)' }}
                  >
                    <SearchIcon size={16} className="shrink-0" style={{ color: '#7fe6ff' }} />
                    <input
                      id="phone-book-search"
                      ref={searchRef}
                      value={q}
                      onChange={(e) => setQ(e.target.value)}
                      placeholder="Address, name, UD, @handle, WhatsApp…"
                      className="min-w-0 flex-1 bg-transparent text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none"
                      inputMode="search"
                      autoComplete="off"
                    />
                    <kbd className="hidden shrink-0 rounded border border-white/12 bg-white/[0.05] px-1.5 py-0.5 text-[10px] font-semibold text-slate-400 sm:block">
                      /
                    </kbd>
                  </div>
                  <div className="pr-actions">
                    <button
                      type="button"
                      onClick={() => setQ('')}
                      disabled={!q}
                      className="pr-btn pr-btn-ghost pr-btn-sm"
                    >
                      Clear
                    </button>
                  </div>
                </div>

                <div className="mt-4 flex flex-wrap items-center gap-x-3 gap-y-2">
                  <span className="pr-hint">
                    {loading
                      ? 'Searching…'
                      : total
                        ? `${total} ${total === 1 ? 'profile' : 'profiles'}${q ? ` matching “${q}”` : ''}`
                        : q
                          ? `No profiles matching “${q}”`
                          : 'No profiles listed yet'}
                  </span>
                  {data?.resolvedAddress && (
                    <span className="pr-badge pr-badge-info">Resolved to {short(data.resolvedAddress)}</span>
                  )}
                </div>
              </div>

              {error ? (
                <div className="pr-panel mt-5 text-center">
                  <div className="pr-panel-title">Couldn&rsquo;t load the directory</div>
                  <p className="pr-hint mx-auto mt-2 max-w-md">{error}</p>
                  <div className="pr-actions mt-5 justify-center">
                    <button type="button" onClick={() => load(page, q)} className="pr-btn pr-btn-primary pr-btn-sm">
                      Try again
                    </button>
                  </div>
                </div>
              ) : null}

              {/* Results */}
              <div
                className="mt-5 grid grid-cols-1 items-stretch gap-4 sm:grid-cols-2 xl:grid-cols-3"
                aria-busy={loading}
              >
                {showSkeletons
                  ? /* Skeletons mirror the real card so the grid doesn't jump. */
                    Array.from({ length: 6 }).map((_, i) => (
                      <div key={i} className="pr-contact">
                        <div className="flex items-center gap-3">
                          <div className="h-11 w-11 shrink-0 rounded-full bg-white/[0.07]" />
                          <div className="min-w-0 flex-1">
                            <div className="mb-2 h-4 w-2/3 rounded bg-white/[0.07]" />
                            <div className="h-3 w-1/2 rounded bg-white/[0.07]" />
                          </div>
                        </div>
                        <div className="pr-contact-body mt-4">
                          <div className="mb-2 h-3 w-full rounded bg-white/[0.07]" />
                          <div className="h-3 w-3/5 rounded bg-white/[0.07]" />
                        </div>
                        <div className="pr-contact-foot">
                          <div className="h-3 w-2/5 rounded bg-white/[0.07]" />
                        </div>
                      </div>
                    ))
                  : results.map((p) => {
                      const site = safeWebsite(p.website);
                      const x = p.twitter ? twitterUrl(p.twitter) : null;
                      const tg = p.telegram ? telegramUrl(p.telegram) : null;
                      const walletHref = `/wallet/ethereum/${encodeURIComponent(p.address)}`;
                      return (
                        <article key={p.address} className="pr-contact">
                          <div className="flex items-start gap-3">
                            {/* FIX-49: real avatar when set, otherwise a
                                WalletIdenticon keyed on this entry's address. */}
                            {p.avatarUrl ? (
                              <Image
                                src={p.avatarUrl}
                                alt=""
                                className="h-11 w-11 shrink-0 rounded-full border border-white/12 object-cover"
                                width={44}
                                height={44}
                              />
                            ) : (
                              <WalletIdenticon
                                address={p.address}
                                size={44}
                                className="h-11 w-11 shrink-0 rounded-full border border-white/12"
                              />
                            )}
                            <div className="min-w-0 flex-1">
                              <Link href={walletHref} className="pr-contact-name">
                                {p.displayName || p.udDomain || short(p.address)}
                              </Link>
                              <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-slate-400">
                                {p.udDomain && <span className="truncate">{p.udDomain}</span>}
                                <span className="font-mono">{short(p.address)}</span>
                              </div>
                            </div>
                          </div>

                          <div className="pr-contact-body mt-3.5">
                            {p.bio ? <p className="pr-contact-bio">{p.bio}</p> : null}

                            {/* Real links, finally. These couldn't exist while
                                the whole card was a single anchor. */}
                            {(x || tg || site || p.discord || p.phoneApps?.length) && (
                              <div className={`flex flex-wrap gap-1.5 ${p.bio ? 'mt-3' : ''}`}>
                                {x && (
                                  <a
                                    href={x}
                                    target="_blank"
                                    rel="noopener noreferrer nofollow"
                                    className="pr-contact-chip"
                                    title={`Open ${handleOnly(p.twitter || '')} on X`}
                                  >
                                    <span>X · @{handleOnly(p.twitter || '')}</span>
                                  </a>
                                )}
                                {tg && (
                                  <a
                                    href={tg}
                                    target="_blank"
                                    rel="noopener noreferrer nofollow"
                                    className="pr-contact-chip"
                                    title={`Open ${handleOnly(p.telegram || '')} on Telegram`}
                                  >
                                    <span>TG · @{handleOnly(p.telegram || '')}</span>
                                  </a>
                                )}
                                {site && (
                                  <a
                                    href={site}
                                    target="_blank"
                                    rel="noopener noreferrer nofollow"
                                    className="pr-contact-chip"
                                    title={site}
                                  >
                                    <GlobeIcon size={11} className="shrink-0" />
                                    <span>{site.replace(/^https?:\/\/(www\.)?/, '').replace(/\/$/, '')}</span>
                                  </a>
                                )}
                                {/* Discord usernames have no public URL, so this
                                    stays a chip - but a copyable one. */}
                                {p.discord && (
                                  <button
                                    type="button"
                                    onClick={() => copyAddress(p.discord as string)}
                                    className="pr-contact-chip"
                                    title={`Copy Discord: ${p.discord}`}
                                  >
                                    <span>Discord · {p.discord}</span>
                                  </button>
                                )}
                                {p.phoneApps?.slice(0, 3).map((app) => (
                                  <span key={app} className="pr-contact-chip">
                                    <span>{app}</span>
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>

                          <div className="pr-contact-foot">
                            <div className="pr-actions">
                              <Link href={walletHref} className="pr-btn pr-btn-ghost pr-btn-sm">
                                View wallet
                              </Link>
                              <button
                                type="button"
                                onClick={() => copyAddress(p.address)}
                                className="pr-btn pr-btn-ghost pr-btn-sm"
                                aria-label={`Copy address ${p.address}`}
                              >
                                {copied === p.address ? (
                                  <>
                                    <CheckCircleIcon size={13} />
                                    <span>Copied</span>
                                  </>
                                ) : (
                                  <>
                                    <CopyIcon size={13} />
                                    <span>Copy address</span>
                                  </>
                                )}
                              </button>
                            </div>
                          </div>
                        </article>
                      );
                    })}
              </div>

              {!loading && !error && results.length === 0 ? (
                <div className="mt-5 flex flex-col items-center gap-3 rounded-[1.35rem] border border-dashed border-white/12 px-6 py-14 text-center">
                  <PhoneIcon size={22} style={{ color: '#7fe6ff' }} />
                  <p className="max-w-md text-sm text-slate-300">
                    {q
                      ? `Nothing matched “${q}”. Try a wallet address, an Unstoppable Domain, or a social handle.`
                      : 'No profiles are listed yet. Be the first to opt in from your profile settings.'}
                  </p>
                  {q ? (
                    <div className="pr-actions justify-center">
                      <button type="button" onClick={() => setQ('')} className="pr-btn pr-btn-ghost pr-btn-sm">
                        Clear search
                      </button>
                    </div>
                  ) : null}
                </div>
              ) : null}

              {/* Pagination */}
              {maxPage > 1 ? (
                <div className="mt-7 flex flex-wrap items-center justify-between gap-3 border-t border-white/[0.08] pt-5">
                  <div className="pr-hint">
                    Page {page} of {maxPage} · {total} {total === 1 ? 'profile' : 'profiles'}
                  </div>
                  <div className="pr-actions">
                    <button
                      type="button"
                      disabled={page <= 1 || loading}
                      onClick={() => {
                        const next = Math.max(1, page - 1);
                        setPage(next);
                        void load(next, q);
                      }}
                      className="pr-btn pr-btn-ghost pr-btn-sm"
                    >
                      Previous
                    </button>
                    <button
                      type="button"
                      disabled={page >= maxPage || loading}
                      onClick={() => {
                        const next = Math.min(maxPage, page + 1);
                        setPage(next);
                        void load(next, q);
                      }}
                      className="pr-btn pr-btn-ghost pr-btn-sm"
                    >
                      Next
                    </button>
                  </div>
                </div>
              ) : null}
            </div>

            {/* ── Sidebar ─────────────────────────────────────────────── */}
            <aside className="pr-stack w-full min-w-0">
              <div className="pr-panel">
                <div className="pr-panel-head">
                  <span className="pr-panel-icon">
                    <PhoneIcon size={16} />
                  </span>
                  <span className="pr-panel-title">Get listed</span>
                  <p className="pr-panel-sub">
                    Listing is opt-in and reversible — turn it off any time and your profile disappears from here.
                  </p>
                </div>
                <div className="pr-actions">
                  <Link href="/profile/edit#phone-book-listing" className="pr-btn pr-btn-primary pr-btn-sm">
                    Manage my listing
                  </Link>
                </div>
              </div>

              <div className="pr-panel">
                <DiscordCommunitySection compact />
              </div>
            </aside>
          </div>
        </section>
      </main>
    </div>
  );
}
